import { LightningElement, api } from 'lwc';

import {formatDbAddress} from '../../../utils/dtypeUtils';

const GOOGLE_MAPS_API_KEY = 'AIzaSyC4Vr-YUcad614oCkjf4ju5n6xwQdUm6HQ';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 250;
const DEFAULT_CITY_SUGGESTION_COUNTRY = 'US';
const MX_TIME_ZONES = new Set([
    'America/Bahia_Banderas',
    'America/Cancun',
    'America/Chihuahua',
    'America/Ciudad_Juarez',
    'America/Hermosillo',
    'America/Matamoros',
    'America/Mazatlan',
    'America/Merida',
    'America/Mexico_City',
    'America/Monterrey',
    'America/Ojinaga',
    'America/Tijuana'
]);
const US_TIME_ZONES = new Set([
    'America/Adak',
    'America/Anchorage',
    'America/Boise',
    'America/Chicago',
    'America/Denver',
    'America/Detroit',
    'America/Indiana/Indianapolis',
    'America/Indiana/Knox',
    'America/Indiana/Marengo',
    'America/Indiana/Petersburg',
    'America/Indiana/Tell_City',
    'America/Indiana/Vevay',
    'America/Indiana/Vincennes',
    'America/Indiana/Winamac',
    'America/Juneau',
    'America/Kentucky/Louisville',
    'America/Kentucky/Monticello',
    'America/Los_Angeles',
    'America/Menominee',
    'America/Metlakatla',
    'America/New_York',
    'America/Nome',
    'America/North_Dakota/Beulah',
    'America/North_Dakota/Center',
    'America/North_Dakota/New_Salem',
    'America/Phoenix',
    'America/Sitka',
    'America/Yakutat',
    'Pacific/Honolulu'
]);
const CITY_SUGGESTIONS_BY_COUNTRY = {
    US: [
        {key: 'us-chicago', label: 'Chicago', query: 'Chicago, IL, USA'},
        {key: 'us-las-vegas', label: 'Las Vegas', query: 'Las Vegas, NV, USA'},
        {key: 'us-los-angeles', label: 'Los Angeles', query: 'Los Angeles, CA, USA'},
        {key: 'us-phoenix', label: 'Phoenix', query: 'Phoenix, AZ, USA'},
        {key: 'us-salt-lake-city', label: 'Salt Lake City', query: 'Salt Lake City, UT, USA'},
        {key: 'us-houston', label: 'Houston', query: 'Houston, TX, USA'},
        {key: 'us-miami', label: 'Miami', query: 'Miami, FL, USA'}
    ],
    MX: [
        {key: 'mx-mexico-city', label: 'Mexico City', query: 'Mexico City, Mexico'},
        {key: 'mx-puebla', label: 'Puebla', query: 'Puebla, Mexico'},
        {key: 'mx-oaxaca-de-juarez', label: 'Oaxaca de Juárez', query: 'Oaxaca de Juárez, Oaxaca, Mexico'},
        {key: 'mx-san-miguel-de-allende', label: 'San Miguel de Allende', query: 'San Miguel de Allende, Guanajuato, Mexico'},
        {key: 'mx-guadalajara', label: 'Guadalajara', query: 'Guadalajara, Jalisco, Mexico'},
        {key: 'mx-merida', label: 'Mérida', query: 'Mérida, Yucatán, Mexico'},
        {key: 'mx-puerto-vallarta', label: 'Puerto Vallarta', query: 'Puerto Vallarta, Jalisco, Mexico'}
    ]
};

let googleMapsBootstrapPromise;
let googleMapsApiPromise;

function normalizeCountryCode(country) {
    const normalizedCountry = String(country || '').trim().toUpperCase();
    const countryMap = {
        UNITED_STATES: 'US',
        'UNITED STATES': 'US',
        USA: 'US',
        US: 'US',
        MEXICO: 'MX',
        MÉXICO: 'MX',
        MX: 'MX'
    };

    return countryMap[normalizedCountry] || null;
}

function getBrowserLocaleCountryCode() {
    if (typeof navigator === 'undefined') return null;
    if (typeof Intl === 'undefined') return null;

    const language = navigator.languages?.find(Boolean) || navigator.language;
    if (!language) return null;

    try {
        return normalizeCountryCode(new Intl.Locale(language).region);
    } catch (error) {
        const parts = String(language).split(/[-_]/);
        return normalizeCountryCode(parts[1]);
    }
}

function getBrowserTimeZoneCountryCode() {
    if (typeof Intl === 'undefined') return null;

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (MX_TIME_ZONES.has(timeZone)) {
        return 'MX';
    }

    if (US_TIME_ZONES.has(timeZone)) {
        return 'US';
    }

    return null;
}

function loadGoogleMapsScript() {
    if (window.google?.maps?.importLibrary) {
        return Promise.resolve();
    }

    if (googleMapsBootstrapPromise) {
        return googleMapsBootstrapPromise;
    }

    googleMapsBootstrapPromise = Promise.resolve().then(() => {
        const googleNamespace = window.google = window.google || {};
        const mapsNamespace = googleNamespace.maps = googleNamespace.maps || {};
        const libraries = new Set();
        const callbackName = '__ib__';

        if (mapsNamespace.importLibrary) {
            return;
        }

        const loadApi = () => {
            if (!googleMapsApiPromise) {
                googleMapsApiPromise = new Promise((resolve, reject) => {
                    const params = new URLSearchParams();
                    const script = document.createElement('script');

                    params.set('libraries', [...libraries].join(','));
                    params.set('key', GOOGLE_MAPS_API_KEY);
                    params.set('v', 'weekly');
                    params.set('loading', 'async');
                    params.set('callback', `google.maps.${callbackName}`);

                    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
                    script.async = true;
                    script.onerror = () => {
                        googleMapsApiPromise = null;
                        reject(new Error('The Google Maps JavaScript API could not load.'));
                    };
                    script.nonce = document.querySelector('script[nonce]')?.nonce || '';
                    mapsNamespace[callbackName] = resolve;

                    document.head.appendChild(script);
                });
            }

            return googleMapsApiPromise;
        };

        mapsNamespace.importLibrary = (libraryName, ...rest) => {
            libraries.add(libraryName);
            return loadApi().then(() => mapsNamespace.importLibrary(libraryName, ...rest));
        };
    });

    return googleMapsBootstrapPromise;
}


function formatAddressSuggestions(suggestions) {
    // Store each option by key <placeId>
    const predictionsByPlaceId = {};

    const addressOptions = (suggestions || [])
        .filter((suggestion) => suggestion.placePrediction)
        .map((suggestion) => {
            const prediction = suggestion.placePrediction;
            const placeId = prediction.placeId;
            const mainText = prediction.mainText?.text || prediction.text?.text || '';
            const secondaryText = prediction.secondaryText?.text || '';
            const fullText = prediction.text?.text ||
                [mainText, secondaryText].filter(Boolean).join(', ');

            predictionsByPlaceId[placeId] = prediction;

            const fullTextLabel = secondaryText && secondaryText !== fullText
                ? fullText.replace(`, ${secondaryText}`, '')
                : fullText;

            return {
                selectedLabel: fullText,
                label: fullTextLabel,
                value: placeId,
                metaOne: secondaryText && secondaryText !== fullText ? secondaryText : null
            };
        });

    return {
        predictionsByPlaceId,
        addressOptions: addressOptions.map(opt => ({...opt, icon: 'material:location'}))
    };
}

function getSearchMode(place) {
    const types = place.types || [];
    const componentTypes = (place.addressComponents || [])
        .flatMap(component => component.types || []);
    const hasType = (typeList, searchTypes) =>
        searchTypes.some(type => typeList.includes(type));

    if (hasType(types, ['postal_code'])) {
        return 'zip_code';
    }

    if (hasType(types, ['neighborhood', 'sublocality', 'sublocality_level_1'])) {
        return 'neighborhood';
    }

    if (hasType(types, ['locality', 'postal_town'])) {
        return 'city';
    }

    if (hasType(types, ['administrative_area_level_1'])) {
        return 'state';
    }

    if (hasType(types, ['country'])) {
        return 'country';
    }

    if (
        hasType(types, [
            'landmark',
            'tourist_attraction',
            'point_of_interest',
            'establishment',
            'natural_feature',
            'park',
            'museum'
        ])
    ) {
        return 'landmark';
    }

    if (hasType(componentTypes, ['postal_code'])) {
        return 'zip_code';
    }

    if (hasType(componentTypes, ['neighborhood', 'sublocality', 'sublocality_level_1'])) {
        return 'neighborhood';
    }

    if (hasType(componentTypes, ['locality', 'postal_town'])) {
        return 'city';
    }

    return 'landmark';
}

function formatCitySuggestions(suggestions) {
    // Store each option by key <placeId>
    const predictionsByPlaceId = {};

    const cityOptions = (suggestions || [])
        .filter((suggestion) => suggestion.placePrediction)
        .map((suggestion) => {
            const prediction = suggestion.placePrediction;
            const placeId = prediction.placeId;

            const mainText =
                prediction.mainText?.text ||
                prediction.text?.text ||
                '';

            const secondaryText =
                prediction.secondaryText?.text ||
                '';

            predictionsByPlaceId[placeId] = prediction;

            const label = mainText
            const metaOne = secondaryText
            return {
                selectedLabel: label,
                label,
                value: placeId,
                metaOne
            };
        });

    return {
        predictionsByPlaceId,
        addressOptions: cityOptions.map(opt => ({
            ...opt,
            icon: 'material:location_city'
        }))
    };
}


export default class googlePlacesInput extends LightningElement {
    // Make the input element have "required" attribute
    @api required = false;

    // address | city | state | place
    @api variant = 'address'; 
    @api coordinates = {
        latitude: null,
        longitude: null,
    };
    @api country;
    _displaySuggestions = false;
    
    comboboxCurrentValue;
    pendingParentAddressLabel;
    parentAddressLookupSeq = 0;

    // Search combobox styling
    searchBoxConfig = {
        fontSize: "1rem",
        placeHolder: "Search address...",
        inputHeight: "3.0rem",
        borderRadius: "var(--slds-g-radius-border-3, 0.75rem)",
        isSelectionVariant: true,
        optionsBordered: true,

        suppressIcon: false,
        dropdownDirection: null,
        suppressBorder: false,

        suppressNoMatch: true,
        suppressSelectedIcon: true,
        dropdownGrow: false,
        suppressErrorText: false,
        allowNull: false,
    };
    @api 
    set comboboxStyles(val) {
        this._comboboxStyles = val;
        if (val && typeof val === 'object' && Object.keys(val)?.length) {
            this.searchBoxConfig = Object.fromEntries(
                Object.entries(this.searchBoxConfig).map( ([prop, propVal]) => 
                    [ prop, val[prop] ?? propVal ]
                )
            );
        }
    }
    get comboboxStyles() {
        return this._comboboxStyles;
    }

    @api
    set displaySuggestions(val) {
        this._displaySuggestions = val === true || val === 'true';

        if (this._displaySuggestions && this.initialized) {
            this.loadDisplaySuggestions();
        }
    }
    get displaySuggestions() {
        return this._displaySuggestions;
    }

    get citySuggestionCountryCode() {
        return normalizeCountryCode(this.country) ||
            getBrowserTimeZoneCountryCode() ||
            getBrowserLocaleCountryCode() ||
            DEFAULT_CITY_SUGGESTION_COUNTRY;
    }

    get citySuggestions() {
        if (!this.displaySuggestions) {
            return [];
        }

        return CITY_SUGGESTIONS_BY_COUNTRY[this.citySuggestionCountryCode] ||
            CITY_SUGGESTIONS_BY_COUNTRY[DEFAULT_CITY_SUGGESTION_COUNTRY];
    }


    @api 
    set currentValue(val) {
        this._currentValue = val;

        if (val) {
            this.pendingParentAddressLabel = formatDbAddress(val);
            this.syncParentAddressValue();
        } else {
            this.pendingParentAddressLabel = null;
            this.comboboxCurrentValue = null;
            this.addressOptions = [];
            this.predictionsByPlaceId = {};
            this.loadDisplaySuggestions();
        }
    }
    get currentValue() {
        return this._currentValue;
    }

    
    removeInitialAddress() {
        this.comboboxCurrentValue = null;
        this.pendingParentAddressLabel = null;
        this.addressOptions = [];
    }

    initialized = false;
    selectedPlace;
    selectedAddress = '';
    addressInput = '';
    addressOptions = [];
    predictionsByPlaceId = {};
    sessionToken;
    placesLibrary;
    debounceTimer;
    requestSeq = 0;

    
    
    // ======================= 🆕 Get City/State/etc. from lng/lat ======================== \\
    // 🆕 **NEW**
    async reverseGeocodeCoordinates(latitude, longitude) {
        await loadGoogleMapsScript();

        const geocoder = new google.maps.Geocoder();

        return new Promise((resolve, reject) => {
            geocoder.geocode(
                {
                    location: {
                        lat: Number(latitude),
                        lng: Number(longitude)
                    }
                },
                (results, status) => {
                    if (status !== 'OK' || !results?.length) {
                        reject(status);
                        return;
                    }

                    resolve(results[0]);
                }
            );
        });
    }
    @api // 🆕 **NEW**
    async resolveLocationFromCoordinates() {
        if (!this.latitude || !this.longitude) {
            this.dispatchPlaceChange(null);
            return;
        }

        const result = await this.reverseGeocodeCoordinates(this.latitude, this.longitude);

        const payload = {
            placeId: result.place_id,
            displayName: result.formatted_address,
            formattedAddress: result.formatted_address,
            addressComponents: (result.address_components || []).map(component => ({
                longText: component.long_name,
                shortText: component.short_name,
                types: component.types
            })),
            latitude: Number(this.latitude),
            longitude: Number(this.longitude)
        };

        this.dispatchPlaceChange(payload);
    }
    // 🆕 **NEW** --> Handle which properties Place API returns based on "variant" value
    getAutocompleteRequest(input) {
        const request = {
            input,
            includedRegionCodes: ['us', 'mx'],
            sessionToken: this.sessionToken
        };

        if (this.variant === 'address') {
            request.includedPrimaryTypes = ['street_address', 'premise', 'subpremise'];
        }

        if (this.variant === 'city') {
            request.includedPrimaryTypes = ['(cities)'];
        }

        if (this.variant === 'state') {
            request.includedPrimaryTypes = ['administrative_area_level_1'];
        }

        if (this.variant === 'place') {
            // Leave unrestricted, or restrict later by type if needed.
        }

        return request;
    }
    // 🆕 **NEW** --> Format options sent to combobox based on the "variant" value
    formatPlacesOptions(suggestions) {

    }
    // ==================================================================================== \\


    renderedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.ensurePlacesLibrary().then(() => {
            if (this.pendingParentAddressLabel) {
                this.syncParentAddressValue();
                return;
            }

            this.loadDisplaySuggestions();
        }).catch((error) => {
            console.error('Error loading Google Places library in googlePlacesInput.js', error);
            this.showToast(
                'Address search unavailable',
                'We were unable to load address search. Please try again.',
                'error'
            );
        });
    }

    async ensurePlacesLibrary() {
        if (this.placesLibrary) {
            return this.placesLibrary;
        }

        await loadGoogleMapsScript();
        this.placesLibrary = await google.maps.importLibrary('places');
        return this.placesLibrary;
    }

    handleAddressInput(event) {
        event.stopPropagation();
        const input = event.detail || '';
        this.comboboxCurrentValue = null;
        this.pendingParentAddressLabel = null;
        this.addressInput = input;

        window.clearTimeout(this.debounceTimer);

        if (input.trim().length < MIN_SEARCH_LENGTH) {
            this.addressOptions = [];
            this.predictionsByPlaceId = {};
            this.selectedPlace = null;
            this.selectedAddress = '';
            this.comboboxCurrentValue = null;
            this.sessionToken = null;
            this.dispatchPlaceChange(null);
            this.loadDisplaySuggestions();
            return;
        }

        this.debounceTimer = window.setTimeout(() => {
            this.fetchAddressPredictions(input);
        }, SEARCH_DEBOUNCE_MS);
    }

    async fetchAddressPredictions(input) {
        const currentSeq = ++this.requestSeq;

        try {
            const { addressOptions, predictionsByPlaceId } = await this.getAddressPredictions(input);

            if (currentSeq !== this.requestSeq) {
                return;
            }

            this.predictionsByPlaceId = predictionsByPlaceId;
            this.addressOptions = addressOptions || [];
        } catch (error) {
            console.error('Error fetching address predictions in googlePlacesInput.js', error);
            this.addressOptions = [];
            this.predictionsByPlaceId = {};
        }
    }

    async loadDisplaySuggestions() {
        if (!this.displaySuggestions || this.addressInput || this.comboboxCurrentValue) {
            return;
        }

        const suggestions = this.citySuggestions;
        if (!suggestions.length) {
            return;
        }

        const currentSeq = ++this.requestSeq;

        try {
            const { AutocompleteSuggestion, AutocompleteSessionToken } = await this.ensurePlacesLibrary();
            const sessionToken = new AutocompleteSessionToken();
            const countryCode = this.citySuggestionCountryCode.toLowerCase();
            this.sessionToken = sessionToken;

            const results = await Promise.allSettled(
                suggestions.map(async city => {
                    const { suggestions: placeSuggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
                        input: city.query,
                        includedPrimaryTypes: ['(cities)'],
                        includedRegionCodes: [countryCode],
                        sessionToken
                    });

                    return placeSuggestions || [];
                })
            );

            if (currentSeq !== this.requestSeq || this.addressInput || this.comboboxCurrentValue) {
                return;
            }

            const { addressOptions, predictionsByPlaceId } = formatCitySuggestions(
                results
                    .filter(result => result.status === 'fulfilled')
                    .flatMap(result => result.value)
            );
            const seenValues = new Set();
            const uniqueOptions = addressOptions.filter(option => {
                if (!option.value || seenValues.has(option.value)) {
                    return false;
                }

                seenValues.add(option.value);
                return true;
            });

            this.predictionsByPlaceId = predictionsByPlaceId;
            this.addressOptions = uniqueOptions;
        } catch (error) {
            console.error('Error loading display suggestions in googlePlacesInput.js', error);
        }
    }

    async getAddressPredictions(input) {
        const {
            AutocompleteSuggestion,
            AutocompleteSessionToken
        } = await this.ensurePlacesLibrary();

        if (!this.sessionToken) {
            this.sessionToken = new AutocompleteSessionToken();
        }

        // ⚠️ **OLD**
        //const request = {input, includedRegionCodes: ['us', 'mx'], sessionToken: this.sessionToken};
        // 🆕 **NEW**
        const request = this.getAutocompleteRequest(input);


        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        /* ⚠️ **OLD**
        const predictionsByPlaceId = {};

        const addressOptions = (suggestions || [])
            .filter((suggestion) => suggestion.placePrediction)
            .map((suggestion) => {
                const prediction = suggestion.placePrediction;
                const placeId = prediction.placeId;
                const mainText = prediction.mainText?.text || prediction.text?.text || '';
                const secondaryText = prediction.secondaryText?.text || '';
                const fullText = prediction.text?.text ||
                    [mainText, secondaryText].filter(Boolean).join(', ');

                predictionsByPlaceId[placeId] = prediction;

                const fullTextLabel = secondaryText && secondaryText !== fullText
                    ? fullText.replace(`, ${secondaryText}`, '')
                    : fullText;

                return {
                    selectedLabel: fullText,
                    label: fullTextLabel,
                    value: placeId,
                    metaOne: secondaryText && secondaryText !== fullText ? secondaryText : null
                };
            });

        return {
            predictionsByPlaceId,
            addressOptions: addressOptions.map(opt => ({...opt, icon: 'material:location'}))
        };
        */
        // 🆕 **NEW**
        switch(this.variant) {
            case 'address':
                return formatAddressSuggestions(suggestions);
            case 'city':
                return formatCitySuggestions(suggestions);
            case 'place':
                return formatAddressSuggestions(suggestions);
            default:
                return {
                    predictionsByPlaceId: {},
                    addressOptions: []
                };
        }
    }

    async syncParentAddressValue() {
        const addressLabel = this.pendingParentAddressLabel;
        if (!addressLabel || addressLabel.trim().length < MIN_SEARCH_LENGTH) return;

        const currentSeq = ++this.parentAddressLookupSeq;

        try {
            const { addressOptions, predictionsByPlaceId } = await this.getAddressPredictions(addressLabel);

            if (currentSeq !== this.parentAddressLookupSeq || this.pendingParentAddressLabel !== addressLabel) {
                return;
            }

            this.predictionsByPlaceId = predictionsByPlaceId;
            this.addressOptions = addressOptions;

            const selectedOption = this.findBestAddressOption(addressOptions, addressLabel);
            if (!selectedOption) {
                this.comboboxCurrentValue = {
                    label: addressLabel,
                    selectedLabel: addressLabel,
                    value: addressLabel
                };
                return;
            }

            this.comboboxCurrentValue = selectedOption;
            this.template
                .querySelector('x-slds-combobox-search')
                ?.setValue(selectedOption.value, selectedOption.label, selectedOption.selectedLabel);
        } catch (error) {
            console.error('Error syncing parent address in googlePlacesInput.js', error);
            this.comboboxCurrentValue = {
                label: addressLabel,
                selectedLabel: addressLabel,
                value: addressLabel
            };
        }
    }

    findBestAddressOption(addressOptions, addressLabel) {
        const normalizedLabel = this.normalizeAddressLabel(addressLabel);

        return addressOptions.find(option =>
            this.normalizeAddressLabel(option.selectedLabel) === normalizedLabel ||
            this.normalizeAddressLabel(`${option.label}, ${option.metaOne || ''}`) === normalizedLabel
        ) || addressOptions[0] || null;
    }

    normalizeAddressLabel(label) {
        return String(label || '')
            .toLowerCase()
            .replace(/\s+/g, ' ')
            .replace(/\s+,/g, ',')
            .trim();
    }

    async handleAddressSelect(event) {
        event.stopPropagation();
        const placeId = event.detail;

        if (!placeId) {
            window.clearTimeout(this.debounceTimer);
            this.selectedPlace = null;
            this.selectedAddress = '';
            this.addressInput = '';
            this.comboboxCurrentValue = null;
            this.addressOptions = [];
            this.predictionsByPlaceId = {};
            this.sessionToken = null;
            this.dispatchPlaceChange(null);
            return;
        }

        await this.selectAddress(placeId);
    }

    async selectAddress(placeId) {
        const prediction = this.predictionsByPlaceId[placeId];

        if (!prediction) {
            return;
        }

        try {
            const place = prediction.toPlace();

            await place.fetchFields({
                fields: [
                    'id',
                    'displayName',
                    'formattedAddress',
                    'addressComponents',
                    'location',
                    'types'
                ]
            });

            const payload = {
                placeId: place.id,
                displayName: place.displayName,
                formattedAddress: place.formattedAddress,
                addressComponents: (place.addressComponents || []).map(component => ({
                    longText: component.longText,
                    shortText: component.shortText,
                    types: component.types
                })),
                types: place.types || [],
                latitude: place.location?.lat(),
                longitude: place.location?.lng()
            };
            

            this.selectedPlace = payload;
            this.selectedAddress = this.variant === 'city'
                ? prediction.text?.text || payload.formattedAddress || ''
                : payload.formattedAddress || prediction.text?.text || '';
            this.addressInput = this.selectedAddress;
            this.comboboxCurrentValue = {
                label: this.selectedAddress,
                selectedLabel: this.selectedAddress,
                value: placeId
            };
            this.template
                .querySelector('x-slds-combobox-search')
                ?.setValue(placeId, this.selectedAddress);
            this.addressOptions = [];
            this.predictionsByPlaceId = {};
            this.sessionToken = null;
            this.dispatchPlaceChange(payload);
        } catch (error) {
            console.error('Error selecting address in googlePlacesInput.js', error);
            this.showToast(
                'Address search unavailable',
                'We were unable to select that address. Please try again.',
                'error'
            );
        }
    }


    get menuTitle() {
        return this._displaySuggestions && !this.addressInput
            ? 'Suggested destinations'
            : `Results matching "${this.addressInput}"...`;

    }

    /* ⚠️ **OLD**
    dispatchPlaceChange(place) {
        if (!place) {
            this.dispatchEvent(new CustomEvent('change', {
                detail: null,
                bubbles: true,
                composed: true
            }));
            return;
        }

        const mappedAddress = {
            street: '',
            country: '',
            state: '',
            city: '',
            zip_code: '',
            latitude: place.latitude,
            longitude: place.longitude
        };

        let streetNumber = '';
        let route = '';

        (place.addressComponents || []).forEach(comp => {
            const types = comp.types || [];

            if (types.includes('street_number')) {
                streetNumber = comp.longText;
            }

            if (types.includes('route')) {
                route = comp.longText;
            }

            if (types.includes('locality')) {
                mappedAddress.city = comp.longText;
            }

            if (types.includes('administrative_area_level_1')) {
                mappedAddress.state = comp.shortText;
            }

            if (types.includes('country')) {
                mappedAddress.country = comp.shortText;
            }

            if (types.includes('postal_code')) {
                mappedAddress.zip_code = comp.longText;
            }
        });

        mappedAddress.street = `${streetNumber} ${route}`.trim();

        console.log('here is mappedAddress in dispatchPlaceChange() of googlePlacesInput.js: ', JSON.stringify(mappedAddress));

        this.dispatchEvent(new CustomEvent('change', {
            detail: mappedAddress,
            bubbles: true,
            composed: true
        }));
    }
    */
    // 🆕 **NEW**
    dispatchPlaceChange(place) {
        if (!place) {
            this.dispatchEvent(new CustomEvent('change', {
                detail: null,
                bubbles: true,
                composed: true
            }));
            return;
        }

        const mappedAddress = {
            street: '',
            country: '',
            state: '',
            city: '',
            zip_code: '',
            latitude: place.latitude,
            longitude: place.longitude
        };

        let streetNumber = '';
        let route = '';

        (place.addressComponents || []).forEach(comp => {
            const types = comp.types || [];

            if (types.includes('street_number')) {
                streetNumber = comp.longText;
            }

            if (types.includes('route')) {
                route = comp.longText;
            }

            if (types.includes('locality')) {
                mappedAddress.city = comp.longText;
            }

            if (!mappedAddress.city && types.includes('postal_town')) {
                mappedAddress.city = comp.longText;
            }

            if (!mappedAddress.city && types.includes('administrative_area_level_2')) {
                mappedAddress.city = comp.longText;
            }

            if (types.includes('administrative_area_level_1')) {
                mappedAddress.state = comp.shortText;
            }

            if (types.includes('country')) {
                mappedAddress.country = comp.shortText;
            }

            if (types.includes('postal_code')) {
                mappedAddress.zip_code = comp.longText;
            }
        });

        mappedAddress.street = `${streetNumber} ${route}`.trim();

        let detail = mappedAddress;

        if (this.variant === 'city') {
            detail = {
                city: mappedAddress.city,
                state: mappedAddress.state,
                country: mappedAddress.country,
                latitude: mappedAddress.latitude,
                longitude: mappedAddress.longitude
            };
        }

        if (this.variant === 'state') {
            detail = {
                state: mappedAddress.state,
                country: mappedAddress.country,
                latitude: mappedAddress.latitude,
                longitude: mappedAddress.longitude
            };
        }

        if (this.variant === 'place') {
            detail = {
                placeId: place.placeId,
                displayName: place.displayName,
                formattedAddress: place.formattedAddress,
                searchMode: getSearchMode(place),
                city: mappedAddress.city,
                state: mappedAddress.state,
                country: mappedAddress.country,
                latitude: mappedAddress.latitude,
                longitude: mappedAddress.longitude
            };
        }

        this.dispatchEvent(new CustomEvent('change', {
            detail,
            bubbles: true,
            composed: true
        }));
    }

    toastObj;
    showToast(title, message, variant, mode=null) {
        const modeP = mode
            ? mode
                : variant === 'success'
            ? 'dismissible'
            : 'sticky';

        this.toastObj = {
            variant: variant,
            mode: modeP,
            message: message,
            header: {
                text: title
            }
        };
    }
}
